import type { Page, ScrollProp } from "@inertiajs/core";
import { router } from "@inertiajs/react";

const SCROLL_RESTORE_HEADER = "X-Inertia-Scroll-Restore";
const RESET_HEADER = "X-Inertia-Reset";

/**
 * The current page's infinite-scroll positions. Module-scoped so the per-visit
 * header stamp — which runs outside React — can read them. The SSR bundle
 * shares this module graph: the guards below keep it inert there.
 */
let scrollProps: Partial<Record<string, ScrollProp>> = {};

export function initScrollRestore(page: Pick<Page, "scrollProps">): void {
  if (typeof window === "undefined") return;

  scrollProps = page.scrollProps ?? {};
  router.on("success", (event) => {
    scrollProps = event.detail.page.scrollProps ?? {};
  });
}

/**
 * Stamped on every mutating Inertia visit. A mutation's redirect re-renders
 * the page it came from as a full visit, which replaces every scroll prop with
 * its first page — the loaded list collapses and the browser clamps the scroll
 * position. These headers ask the server to re-render the whole loaded range
 * (`X-Inertia-Scroll-Restore: trips=3`) and tell inertia_rails to flag the
 * scroll prop reset so the client InfiniteScroll cursor resyncs instead of
 * resuming from a now-wrong next page.
 *
 * Non-GET only, and load-bearing: GETs include the InfiniteScroll page fetches
 * themselves and prefetches, which the reset header would wipe.
 */
export function scrollRestoreHeaders({ method }: { method?: string }): Record<string, string> {
  if (typeof window === "undefined") return {};
  if (!method || method.toLowerCase() === "get") return {};

  const deep = Object.entries(scrollProps).filter(
    (entry): entry is [string, ScrollProp] => Number(entry[1]?.currentPage) > 1,
  );
  if (deep.length === 0) return {};

  return {
    [SCROLL_RESTORE_HEADER]: deep.map(([prop, meta]) => `${prop}=${meta.currentPage}`).join(","),
    [RESET_HEADER]: deep.map(([prop]) => prop).join(","),
  };
}
